import React, { useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import axiosConn from 'config/axiosConn';

const ageGroups = [
    { range: '3 to 8' },
    { range: '8 to 11' },
    { range: '11 to 14' },
    { range: '14 to 18' },
    { range: '18+' }
];

const themes = [
    'Adopt Healthy Lifestyles', 
    'Adopt sustainable food systems', 
    'Reduce E-Waste', 
    'Reduce Waste', 
    'Save Energy', 
    'Save Water', 
    'Say No to Single Use Plastic'
];

const ApexChartHeatmap = (props) => {
    const [selectedData, setSelectedData] = React.useState(null);
    const [participantCount, setParticipantCount] = React.useState(0);
    const chartRef = React.useRef(null);
    const { state } = props; // Destructure 'state' from props

    const [chartOptions, setChartOptions] = React.useState({
        series: themes.map((theme) => ({
            name: theme,
            data: ageGroups.map((group) => ({
                x: group.range,
                y: 0,  // Initialize with 0, will be updated with actual data
            })),
        })),
        options: {
            chart: {
                height: 350,
                type: 'heatmap',
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        const selectedAgeGroup = config.w.config.xaxis.categories[config.dataPointIndex];
                        const selectedTheme = config.w.config.series[config.seriesIndex].name;
                        const selectedParticipantCount = config.w.config.series[config.seriesIndex].data[config.dataPointIndex].y;

                        // Update the selected data and participant count
                        setSelectedData({ ageGroup: selectedAgeGroup, theme: selectedTheme });
                        setParticipantCount(selectedParticipantCount);
                    },
                },
            },
            plotOptions: {
                heatmap: {
                    shadeIntensity: 0.5,
                    radius: 0,
                    useFillColorAsStroke: false,
                    colorScale: {
                        ranges: [], // This will be updated dynamically
                    },
                },
            },
            dataLabels: {
                enabled: true,
            },
            stroke: {
                width: 1,
            },
            title: {
                text: `Eco Clubs for Mission LiFE theme vs Age group`,
                align: 'left',
                offsetX: 40,
                style: {
                    fontSize: '20px',
                    fontWeight: 'bold',
                },
            },
            tooltip: {
                enabled: false,
            },
            xaxis: {
                type: 'category',
                categories: ageGroups.map(group => group.range),
                title: {
                    text: 'Age Groups',
                },
                labels: {
                    style: {
                        fontSize: '10px',
                    },
                },
            },
            yaxis: {
                title: {
                    text: 'Themes',
                },
                categories: themes,
                labels: {
                    style: {
                        fontSize: '10px',
                    },
                    offsetX: -1,
                    formatter: (value) => value.length > 20 ? value.slice(0, 20) + '...' : value,
                },
            },
        },
    });

    // Function to generate dynamic color ranges based on min and max participant count
    const generateColorScale = (minValue, maxValue) => {
        const rangeStep = Math.ceil((maxValue - minValue) / 5); // Divide the range into 5 steps

        return [
            { from: 0, to: 0, name: 'Zero', color: '#808080' },
            { from: minValue, to: minValue + rangeStep, name: 'Low', color: '#FFB300' },
            { from: minValue + rangeStep + 1, to: minValue + 2 * rangeStep, name: 'Moderate', color: '#FFD700' },
            { from: minValue + 2 * rangeStep + 1, to: minValue + 3 * rangeStep, name: 'Medium', color: '#BDB200' },
            { from: minValue + 3 * rangeStep + 1, to: minValue + 4 * rangeStep, name: 'High', color: '#FF4500' },
            { from: minValue + 4 * rangeStep + 1, to: maxValue, name: 'Extreme', color: '#FF0000' },
        ];
    };

    // Function to fetch data for all themes and age groups
    const fetchData = async (selectedTheme = null, selectedAgeGroup = null) => {
        try {
            const response = await fetch('https://ecoclubs.education.gov.in/api/heat-map-age-theme', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ selectedTheme, selectedAgeGroup, state }), // Pass state here
            });

            if (response.ok) {
                const data = await response.json();
                // console.log("API Response Data:", data);

                let minValue = Infinity;
                let maxValue = -Infinity;

                const updatedSeries = themes.map((theme) => {
                    const dataForTheme = data.find(item => item.theme_name === theme);

                    console.log("Data for Theme:", dataForTheme);

                    if (dataForTheme) {
                        const updatedData = ageGroups.map(group => {
                            let participantCountForAgeGroup = 0;

                            // Correctly match the participant count based on the group range
                            switch (group.range) {
                                case '3 to 8':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age3to8 || 0;
                                    break;
                                case '8 to 11':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age8to11 || 0;
                                    break;
                                case '11 to 14':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age11to14 || 0;
                                    break;
                                case '14 to 18':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age14to18 || 0;
                                    break;
                                case '18+':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age18plus || 0;
                                    break;
                                default:
                                    participantCountForAgeGroup = 0;
                            }

                            minValue = Math.min(minValue, participantCountForAgeGroup);
                            maxValue = Math.max(maxValue, participantCountForAgeGroup);

                            return {
                                x: group.range,
                                y: participantCountForAgeGroup,
                            };
                        });

                        return { name: theme, data: updatedData };
                    }

                    return {
                        name: theme,
                        data: ageGroups.map(group => ({
                            x: group.range,
                            y: 0,
                        })),
                    };
                });

                // Generate the dynamic color scale based on min and max values
                const dynamicColorRanges = generateColorScale(minValue, maxValue);

                // Update the chart options with the new series and color scale
                setChartOptions((prevOptions) => ({
                    ...prevOptions,
                    series: updatedSeries,
                    options: {
                        ...prevOptions.options,
                        plotOptions: {
                            ...prevOptions.options.plotOptions,
                            heatmap: {
                                ...prevOptions.options.plotOptions.heatmap,
                                colorScale: {
                                    ranges: dynamicColorRanges,
                                },
                            },
                        },
                    },
                }));

                // Update participant count if theme and age group are selected
                if (selectedTheme && selectedAgeGroup) {
                    const selectedSeries = updatedSeries.find(series => series.name === selectedTheme);
                    const selectedDataPoint = selectedSeries.data.find(dataPoint => dataPoint.x === selectedAgeGroup);
                    setParticipantCount(selectedDataPoint ? selectedDataPoint.y : 0);
                }
            } else {
                console.error('Failed to fetch data: ', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    // Reset selection
    const resetSelection = () => {
        setSelectedData(null);
        setParticipantCount(0); 
        if (chartRef.current) {
            chartRef.current.chart.updateSeries(chartOptions.series); 
        }
    };

    // Fetch data when the component mounts
    useEffect(() => {
        fetchData();
    }, [state]); // Re-fetch data whenever `state` changes

    return (
        <div style={{ position: 'relative', marginBottom: '22px' }}>
            <div id="chart">
                <ReactApexChart
                    ref={chartRef}  
                    options={chartOptions.options}
                    series={chartOptions.series}
                    type="heatmap"
                    height={350}
                />
            </div>

            {selectedData && (
                <div 
                    style={{
                        marginTop: '20px',
                        padding: '15px',
                        backgroundColor: '#f9f9f9',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        maxWidth: '400px',
                        margin: '20px auto',
                    }}
                >
                    <h4 
                        style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '10px',
                            color: '#333',
                            textAlign: 'center',
                        }}
                    >
                        Selected Age Group and Theme
                    </h4>

                    <div 
                        style={{
                            fontSize: '16px',
                            lineHeight: '1.5',
                            color: '#555',
                            padding: '5px 10px',
                            textAlign: 'left',
                        }}
                    >
                        <p 
                            style={{
                                margin: '5px 0',
                            }}
                        >
                            <strong>Age Group:</strong> {selectedData.ageGroup}
                        </p>

                        <p 
                            style={{
                                margin: '5px 0',
                            }}
                        >
                            <strong>Theme:</strong> {selectedData.theme}
                        </p>

                        <p 
                            style={{
                                margin: '5px 0',
                            }}
                        >
                            <strong>Total People in Age Group:</strong> {participantCount}
                        </p>
                    </div>
                </div>
            )}

            <button className='resetButtonbtn'
                onClick={resetSelection} 
                style={{ 
                    position: 'absolute', 
                    top: '1px', 
                    right: '50px', 
                    padding: '5px 10px',  
                    cursor: 'pointer',
                    fontSize: '12px',  
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px'
                }}
             >
                Reset
            </button>
        </div>
    );
};

export default ApexChartHeatmap;
